<template>
	<div class="comment-form elevation-0 pl-3">
		<v-form
			style="width: 100%"
			ref="form"
			v-model="valid"
			lazy-validation
			@submit.prevent="validateAndSubmit"
		>
			<div class="d-content comment-form__text-wrapper">
				<Avatar
					class="comment-form__avatar mr-2"
					:size="40"
					:avatar="this.profile.user_logo"
				/>

				<div class="comment-form__form-area">
					<v-textarea
						color="#000"
						class="comment-form__textarea flex-1"
						auto-grow
						rows="1"
            placeholder="Your comment ..."
						:value="fields.content"
						v-model="fields.content"
						:error-messages="field_errors.content"
						:rules="rules.content"
						required
					></v-textarea>
					<button
						:style="{
							backgroundColor:
								fields.content === '' ? '#9b9b9b' : 'var(--accent-color)',
						}"
						type="button"
						@click="validateAndSubmit"
						class="comment-form__button elevation-0"
					>
						Comment
					</button>
				</div>
			</div>
			<p v-for="(index, error) in errors" :key="index" class="red--text">
				{{ error }}
			</p>
		</v-form>
		<CommentMentionBar
			v-if="isMentionActive"
			:searchString="fields.content"
			:mentions="mentions"
			@setMention="setMention"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import { ruleRequired } from '@/utils/rules'
import Avatar from '@/components/Avatar'
import CommentMentionBar from '@/components/CommentMentionBar.vue'

export default {
	name: 'CommentForm',
	components: {
		CommentMentionBar,
		Avatar,
	},
	mixins: [Form],
	props: {
		isReply: {
			type: Boolean,
			default: false,
		},
		parent: {
			type: Object,
			default: () => ({}),
		},
		post: {
			type: Object,
			default: () => ({}),
		},
		isEdit: {
			type: Boolean,
			default: false,
		},
		comment: {
			type: Object,
			default: () => ({}),
		},
		autoMentionUser: {
			type: Object,
			default: null,
		},
	},
	data: () => ({
		fields: {
			content: '',
		},
		mentions: [],
	}),
	methods: {
		...mapActions('post', ['addReply']),
		...mapActions('forum', ['setActiveComment']),
		setMention(user, number) {
			const splittedContent = this.fields.content.split('@')
			splittedContent[number + 1] = user.visible_username.replace(' ', '')
			this.mentions[number] = user
			this.fields.content = splittedContent.join('@')
		},

		submitForm() {
			this.field_errors = {}
			let result = this.fields.content
			this.mentions.forEach((item) => {
				const startIndex = result.indexOf(item.visible_username.replace(' ', '')) - 1
				if (startIndex >= 0) {
					const endIndex = item.visible_username.replace(' ', '').length + startIndex + 1
					result =
						result.substring(0, startIndex) +
						`@[${item.user_id}|${item.visible_username}]` +
						result.substring(endIndex)
				}
			})

			const {
				comment: { id: parentCommentId },
				post: { thread_id },
			} = this.$props

			return this.addReply({
				thread_id: thread_id,
				reply_text: result,
				parent_id: parentCommentId,
				isPost: this.$route.name === 'posts-by-id',
				thread_title: this.post.thread_title,
			})
				.then(() => {
					this.fields.content = ''
					this.setActiveComment(false)
				})
				.catch((errors) => {
					this.field_errors = errors
				})
		},
	},
	watch: {
		'fields.content': {
			handler() {
				if (this.fields.content) {
					const splittedContent = this.fields.content.split('@')
					splittedContent.forEach((item, index) => {
						if (
							index > 0 &&
							this.mentions[index - 1] &&
							!item.startsWith(
								this.mentions[index - 1].visible_username.replace(' ', '')
							)
						) {
							this.mentions.splice(index - 1, 1)
							if (splittedContent[index].split(' ')[1]) {
								splittedContent[index - 1] =
									splittedContent[index - 1] +
									splittedContent[index].split(' ')[1]
							}
							splittedContent.splice(index, 1)
							this.fields.content = splittedContent.join('@')
						}
					})
				}
			},
		},
	},
	mounted() {
		if (this.autoMentionUser && this.fields.content === '') {
			const username = this.autoMentionUser.visible_username.replace(/\s+/g, '')
			this.fields.content = `@${username} `
			this.mentions.push(this.autoMentionUser)
		}
	},
	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		isMentionActive() {
			return (
				this.fields.content &&
				!this.isCommentAdding &&
				!this.isForumCommentAdding &&
				this.fields.content.split('@').length > 1 + this.mentions.length
			)
		},
		rules() {
			return {
				content: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => !!v.trim().length || 'Text cannot be empty',
				],
			}
		},
	},
}
</script>

<style scoped lang="scss">
.comment-form {
	min-height: 65px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-family: 'Inter', sans-serif;
	font-style: normal;
	background-color: #ffffff !important;
	font-weight: 500;
	font-size: 20px;
	line-height: 24px;
	letter-spacing: 0.5px;
	position: relative;
	&__text-wrapper {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
	&__avatar {
		border-radius: 8px;
	}
	&__form-area {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-right: 12px;
	}

	&__textarea {
		flex: 1 1 auto;
		margin-right: 24px;
		min-width: 0;
		max-width: none !important;
		background-color: #fff !important;
		color: #000;
		border-radius: 10px;

		&:focus {
			.v-input__slot {
				border: 1px solid #000000;
			}
		}

		.v-input__slot {
			box-shadow: none !important;
			border: 1px solid rgba(0, 0, 0, 0.2);
		}
	}

	&__button {
		width: 134px;
		height: 46px;
		display: flex;
		border-radius: 8px;
		align-items: center;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		justify-content: center;
		color: #ffffff;
	}
}
</style>
